import { Alert, Box, Link as MuiLink, Typography } from "@mui/material"
import { Link as RouterLink } from "react-router-dom"

// The shared editor lives outside the CRM sub-app — a relative import, NOT
// `@/`, which the CRM tsconfig remaps to src/crm (same as the reseller tab).
import { AccessEditor } from "../../../../pages/documentation/access/AccessEditor.js"

/**
 * Customer profile ▸ **Documentation** — what THIS customer reads on the
 * support portal (2026-09-30).
 *
 * The same editor as Documentation ▸ Access, with the account already
 * picked, exactly like the reseller profile's tab. The subject is addressed
 * as `crm-<id>` because this page holds the crm-api customer id and
 * portal-api resolves that form itself (`organizations.crm_external_id`).
 *
 * A customer keeps Auto: with no rule, the products it owns decide. Staff
 * Allow / Block rules sit above the customer's RESELLER's own curation (a
 * reseller may share or hide, for its own customers, only what it can read
 * itself) — the editor marks the rows the reseller ruled.
 *
 * Shown only to a holder of `documentation:manage_access` — the tab set is
 * filtered on it the way the money tabs are filtered on `crm:financials`.
 */
export function CustomerDocumentationTab({
  customerId,
  customerName,
}: {
  customerId: string
  customerName: string
}) {
  return (
    <Box sx={{ display: "grid", gap: 2 }} data-cy="customer-documentation-tab">
      <Typography sx={{ color: "text.secondary" }}>
        The documentation as {customerName} sees it on the support portal. With no rule, the
        products it owns decide (Auto). Allow or Block a folder or a single document to overrule that
        — your rule also wins over anything its reseller shared or hid. A folder must also be
        published to customers in the library.{" "}
        <MuiLink component={RouterLink} to="/documentation/access" underline="hover">
          Open Documentation ▸ Access
        </MuiLink>{" "}
        to curate every account side by side.
      </Typography>

      <AccessEditor
        key={customerId}
        subjectType="organization"
        subjectId={`crm-${customerId}`}
        notFound={
          <Alert severity="info" data-cy="customer-documentation-unsynced">
            {customerName} has not reached the support portal yet, so there is nothing to curate.
            Customers sync from the CRM on the next snapshot; until then nobody from this company can
            sign in to the documentation either.
          </Alert>
        }
      />
    </Box>
  )
}
